import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Plus, Search, Building2, Trash2, Pencil, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const constitutions = ["Proprietorship", "Partnership", "LLP", "Private Limited", "Public Company", "Trust", "Society", "Individual"];

export default function Clients() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ client_code: "", client_name: "", constitution: "Proprietorship", status: "Active" });
  const canDelete = me?.role === "Owner" || me?.role === "Partner";

  function openNew() {
    setEditing(null);
    setForm({ client_code: "", client_name: "", constitution: "Proprietorship", status: "Active" });
    setOpen(true);
  }
  function openEdit(c) {
    setEditing(c);
    setForm({ ...c });
    setOpen(true);
  }

  async function load() {
    const { data } = await api.get(`/clients${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    setRows(data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  async function submit(e) {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/clients/${editing.id}`, form);
        toast.success("Client updated");
      } else {
        await api.post("/clients", form);
        toast.success("Client created");
      }
      setOpen(false);
      setEditing(null);
      setForm({ client_code: "", client_name: "", constitution: "Proprietorship", status: "Active" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  }

  async function del(e, id, name) {
    e.stopPropagation();
    e.preventDefault();
    if (!confirm(`Delete client "${name}"? Their tasks & invoices will remain but become orphaned.`)) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Client deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  }

  return (
    <div className="space-y-5" data-testid="clients-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Client Master</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Clients</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <Button data-testid="add-client-btn" onClick={openNew} className="bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1.5" />New Client</Button>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editing ? "Edit Client" : "New Client"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-2 gap-4">
              <div><Label>Client Code *</Label><Input data-testid="client-code-input" required value={form.client_code || ""} onChange={e => setForm({ ...form, client_code: e.target.value })} /></div>
              <div><Label>Client Name *</Label><Input data-testid="client-name-input" required value={form.client_name || ""} onChange={e => setForm({ ...form, client_name: e.target.value })} /></div>
              <div><Label>Trade Name</Label><Input value={form.trade_name || ""} onChange={e => setForm({ ...form, trade_name: e.target.value })} /></div>
              <div><Label>Constitution</Label>
                <Select value={form.constitution || "Proprietorship"} onValueChange={v => setForm({ ...form, constitution: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{constitutions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>PAN</Label><Input className="font-mono uppercase" maxLength={10} value={form.pan || ""} onChange={e => setForm({ ...form, pan: e.target.value.toUpperCase() })} /></div>
              <div><Label>GSTIN</Label><Input className="font-mono uppercase" maxLength={15} value={form.gstin || ""} onChange={e => setForm({ ...form, gstin: e.target.value.toUpperCase() })} /></div>
              <div><Label>TAN</Label><Input className="font-mono uppercase" maxLength={10} value={form.tan || ""} onChange={e => setForm({ ...form, tan: e.target.value.toUpperCase() })} /></div>
              <div><Label>Mobile</Label><Input value={form.mobile || ""} onChange={e => setForm({ ...form, mobile: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={form.email || ""} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>State</Label><Input value={form.state || ""} onChange={e => setForm({ ...form, state: e.target.value })} /></div>
              <div className="col-span-2"><Label>Address</Label><Input value={form.address || ""} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
              <div><Label>Annual Turnover (₹)</Label><Input type="number" value={form.annual_turnover || ""} onChange={e => setForm({ ...form, annual_turnover: parseFloat(e.target.value || 0) })} /></div>
              <div><Label>Status</Label>
                <Select value={form.status || "Active"} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem><SelectItem value="Closed">Closed</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Google Drive folder URL</Label>
                <Input data-testid="drive-folder-input" value={form.drive_folder_url || ""} onChange={e => setForm({ ...form, drive_folder_url: e.target.value })} placeholder="https://drive.google.com/drive/folders/…" className="font-mono" />
                <div className="text-[10px] text-muted-foreground mt-1">Store this client's documents in a Drive folder — Documents tab will link to it.</div>
              </div>
              <DialogFooter className="col-span-2 mt-2">
                <Button data-testid="save-client-btn" type="submit" className="bg-blue-600 hover:bg-blue-700">{editing ? "Save changes" : "Create Client"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-0">
        <div className="p-4 border-b border-border flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="client-search-input" placeholder="Search name, PAN, GSTIN, code…" value={q} onChange={e => setQ(e.target.value)} className="pl-9" />
          </div>
          <div className="text-xs text-muted-foreground font-mono tabular-nums ml-auto">{rows.length} clients</div>
        </div>
        <Table className="stripe-hover">
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Code</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Constitution</TableHead>
              <TableHead>PAN</TableHead>
              <TableHead>GSTIN</TableHead>
              <TableHead>Mobile</TableHead>
              <TableHead className="text-right">Turnover</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center py-10 text-sm text-muted-foreground">
                <Building2 className="w-6 h-6 mx-auto mb-2 opacity-40" />No clients yet.
              </TableCell></TableRow>
            )}
            {rows.map(c => (
              <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
                <TableCell className="font-mono text-xs">{c.client_code}</TableCell>
                <TableCell>
                  <Link to={`/clients/${c.id}`} className="font-medium hover:text-blue-600" data-testid={`client-link-${c.id}`}>{c.client_name}</Link>
                  {c.trade_name && <div className="text-[10px] text-muted-foreground">{c.trade_name}</div>}
                </TableCell>
                <TableCell className="text-xs">{c.constitution}</TableCell>
                <TableCell className="font-mono text-xs">{c.pan || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{c.gstin || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{c.mobile || "—"}</TableCell>
                <TableCell className="text-right font-mono text-xs tabular-nums">
                  {c.annual_turnover ? `₹${(c.annual_turnover / 100000).toFixed(1)}L` : "—"}
                </TableCell>
                <TableCell>
                  <Badge variant={c.status === "Active" ? "default" : "secondary"} className={c.status === "Active" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0" : ""}>{c.status}</Badge>
                </TableCell>
                <TableCell className="flex items-center gap-1">
                  {c.drive_folder_url && (
                    <a href={c.drive_folder_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="text-muted-foreground hover:text-blue-600" title="Open Drive folder">
                      <FolderOpen className="w-3.5 h-3.5" />
                    </a>
                  )}
                  <button data-testid={`edit-client-${c.id}`} onClick={(e) => { e.stopPropagation(); openEdit(c); }} className="text-muted-foreground hover:text-blue-600 transition-colors">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  {canDelete && (
                    <button data-testid={`delete-client-${c.id}`} onClick={(e) => del(e, c.id, c.client_name)} className="text-muted-foreground hover:text-red-600 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
